<script lang="ts">
const dayOptions = arrayRangeLabelValue(1, 31, 1)
const monthOptions = arrayRangeLabelValue(1, 12, 1)
const currentYear = new Date().getFullYear()
const defaultYearOptions = arrayRangeLabelValue(currentYear, currentYear - 100, -1)
</script>

<script setup lang="ts">
import { useField } from 'vee-validate'
import { Options } from './inputHelper'
import { computed } from 'vue'

type DatePickerProps = {
  label: string
  yearList?: Options
  isYearRequired?: boolean
  monthOnly?: boolean
  day?: string | number
  month?: string | number
  year?: string | number
  // Suffixed with -day, -month and -year for each select.
  testIdPrefix?: string
  attempt?: number
}

const props = defineProps<DatePickerProps>()
const emit = defineEmits(['update:day', 'update:month', 'update:year'])
const { locale } = useI18n()
const birthMonthOptions = computed(() => {
  const localeCode = localeConfig[locale.value]?.code ?? locale.value
  const monthFormatter = new Intl.DateTimeFormat(localeCode, { month: 'long', timeZone: 'UTC' })
  return Array.from({ length: 12 }, (_, index) => ({
    label: monthFormatter.format(new Date(Date.UTC(2000, index, 1))),
    value: String(index + 1),
  }))
})

const {
  value: fieldMonth,
  errorMessage: monthError,
  validate: validateMonth,
} = useField('dmy.month', undefined, { validateOnValueUpdate: false })

// Day and year inputs are not rendered in month-only mode, so they must not join the form either.
const dayField = props.monthOnly ? null : useField('dmy.day', undefined, { validateOnValueUpdate: false })
const yearField = props.monthOnly ? null : useField('dmy.year', undefined, { validateOnValueUpdate: false })

const dayError = computed(() => dayField?.errorMessage.value)
const yearError = computed(() => yearField?.errorMessage.value)
const validateDay = async () => (await dayField?.validate()) ?? { valid: true }
const validateYear = async () => (await yearField?.validate()) ?? { valid: true }

const handleBlurValidation = async (
  validator: () => Promise<{ valid: boolean }>,
  fieldName: 'day' | 'month' | 'year',
  event: Event,
) => {
  const formId = event.target instanceof HTMLElement ? event.target.closest('form')?.id : undefined
  if (!formId) {
    await validator()
    return
  }

  await validateOnBlur(validator, fieldName, `#${formId}`, {
    day: dayError.value,
    month: monthError.value,
    year: yearError.value,
  })
}

const dayValue = computed({
  get: () => props.day ?? dayField?.value.value,
  set: (value) => {
    emit('update:day', value)
    if (dayField) dayField.value.value = value
  },
})

const monthValue = computed({
  get: () => props.month ?? fieldMonth.value,
  set: (value) => {
    emit('update:month', value)
    fieldMonth.value = value
  },
})

const yearValue = computed({
  get: () => props.year ?? yearField?.value.value,
  set: (value) => {
    emit('update:year', value)
    if (yearField) yearField.value.value = value
  },
})
</script>

<template>
  <fieldset class="date-picker">
    <legend class="sr-only">{{ label }}</legend>
    <Select
      v-if="!monthOnly"
      v-model="dayValue"
      name="day"
      :select-test-id="testIdPrefix && `${testIdPrefix}-day`"
      :label="$t('my_account.register.inputs.birthday.day.label')"
      required
      :options="dayOptions"
      :error="dayError"
      :attempt="attempt"
      @blur="handleBlurValidation(validateDay, 'day', $event)"
    />
    <Select
      v-model="monthValue"
      name="month"
      :select-test-id="testIdPrefix && `${testIdPrefix}-month`"
      :label="
        $t(
          monthOnly
            ? 'my_account.register.inputs.birthday.birth_month.label'
            : 'my_account.register.inputs.birthday.month.label',
        )
      "
      required
      :options="monthOnly ? birthMonthOptions : monthOptions"
      :error="monthError"
      :attempt="attempt"
      @blur="handleBlurValidation(validateMonth, 'month', $event)"
    />
    <Select
      v-if="!monthOnly"
      v-model="yearValue"
      name="year"
      :select-test-id="testIdPrefix && `${testIdPrefix}-year`"
      :label="$t('my_account.register.inputs.birthday.year.label')"
      :required="isYearRequired"
      :options="yearList || defaultYearOptions"
      :error="yearError"
      :attempt="attempt"
      @blur="handleBlurValidation(validateYear, 'year', $event)"
    />
  </fieldset>
</template>

<style lang="postcss">
.date-picker {
  border: none;
  padding: 0;
  margin: 0;
  width: 100%;
  display: flex;
  gap: var(--space-30);

  .input--select {
    flex: 1;
  }
}
</style>
